import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import { BrowserQRCodeReader } from '@zxing/browser';
import BottomNavigation from './BottomNavigation';

import '../css/PlayerHistory.css';


/* =================================================
   RESULT LABEL
================================================= */

const RESULT_LABELS = {
  WIN: 'ชนะ',
  DRAW: 'เสมอ',
  LOSS: 'แพ้',
  PENDING: 'กำลังเล่น'
};


/* =================================================
   FORMAT DATE
================================================= */

const formatDateTime = value => {
  if (!value) return '-';

  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat(
    'th-TH',
    {
      dateStyle: 'medium',
      timeStyle: 'short'
    }
  ).format(date);
};


/* =================================================
   LOCAL STORAGE USER
================================================= */

const getStoredUser = () => {
  try {
    return JSON.parse(
      localStorage.getItem('user') || 'null'
    );
  } catch {
    return null;
  }
};


/* =================================================
   AVATAR URL
================================================= */

const getAvatarSource = avatarUrl => {
  if (!avatarUrl) return '';

  if (
    /^https?:\/\//i.test(avatarUrl) ||
    avatarUrl.startsWith('data:')
  ) {
    return avatarUrl;
  }

  return (
    `${API_BASE}` +
    `${avatarUrl.startsWith('/') ? '' : '/'}` +
    `${avatarUrl}`
  );
};


/* =================================================
   RESIZE AVATAR
================================================= */

const resizeAvatar = file =>
  new Promise((resolve, reject) => {

    const reader = new FileReader();

    reader.onerror = () => {
      reject(
        new Error(
          'อ่านไฟล์รูปไม่สำเร็จ'
        )
      );
    };

    reader.onload = () => {

      const image = new Image();

      image.onerror = () => {
        reject(
          new Error(
            'ไฟล์นี้ไม่ใช่รูปที่รองรับ'
          )
        );
      };

      image.onload = () => {

        const size = 512;

        const sourceSize =
          Math.min(
            image.naturalWidth,
            image.naturalHeight
          );

        const sourceX =
          (
            image.naturalWidth -
            sourceSize
          ) / 2;

        const sourceY =
          (
            image.naturalHeight -
            sourceSize
          ) / 2;

        const canvas =
          document.createElement(
            'canvas'
          );

        canvas.width = size;
        canvas.height = size;

        const context =
          canvas.getContext('2d');

        context.fillStyle =
          '#fff7fb';

        context.fillRect(
          0,
          0,
          size,
          size
        );

        context.drawImage(
          image,
          sourceX,
          sourceY,
          sourceSize,
          sourceSize,
          0,
          0,
          size,
          size
        );

        resolve(
          canvas.toDataURL(
            'image/jpeg',
            0.84
          )
        );
      };

      image.src =
        reader.result;
    };

    reader.readAsDataURL(file);
  });


/* =================================================
   COMPONENT
================================================= */

function PlayerHistory({ profileMode = false }) {

  /* =================================================
     ROUTER
  ================================================= */

  const {
    groupId,
    userId
  } = useParams();

  const navigate =
    useNavigate();

  const storedUser = getStoredUser();

  const selectedGroupId = Number(
    groupId || localStorage.getItem('last_group_id') || storedUser?.group_id || 0
  );

  const selectedUserId = Number(
    userId || (profileMode ? storedUser?.user_id : 0)
  );


  /* =================================================
     STATE
  ================================================= */

  const [history, setHistory] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState('');

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMessage, setNameMessage] = useState('');
  const [nameDraft, setNameDraft] = useState('');

  const [
    uploadingAvatar,
    setUploadingAvatar
  ] = useState(false);

  const [
    avatarMessage,
    setAvatarMessage
  ] = useState('');

  const avatarInputRef =
    useRef(null);

  const qrVideoRef = useRef(null);
  const qrControlsRef = useRef(null);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [, setCheckinGroupId] = useState('');
  const [checkinMessage, setCheckinMessage] = useState('');
  const [checkinMessageType, setCheckinMessageType] = useState('info');


  /* =================================================
     AUTH STATE
  ================================================= */

  const [authUser, setAuthUser] =
    useState(() => getStoredUser());

  const [authToken, setAuthToken] =
    useState(() =>
      localStorage.getItem('token')
    );


  const isLoggedIn =
    Boolean(
      authToken &&
      authUser
    );


  const isOwner =
    Number(
      authUser?.user_id
    ) === selectedUserId;


  const isAdmin =
    String(
      authUser?.role || ''
    )
      .trim()
      .toLowerCase() === 'admin';


  const canEditAvatar =
    isLoggedIn &&
    (
      isOwner ||
      isAdmin
    );


  /* =================================================
     LOAD HISTORY
  ================================================= */

  const loadHistory =
    useCallback(
      async ({
        silent = false
      } = {}) => {

        if (!selectedUserId) {
          return;
        }

        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        try {

          const response =
            await fetch(
              `${API_BASE}/api/game-match/player-history/${selectedUserId}`
            );

          const data =
            await response
              .json()
              .catch(() => ({}));

          if (!response.ok) {
            throw new Error(
              data.message ||
              `HTTP ${response.status}`
            );
          }

          setHistory(data);
          setError('');

        } catch (loadError) {

          console.error(
            '[PLAYER HISTORY]',
            loadError
          );

          setError(
            loadError.message ||
            'โหลดประวัติผู้เล่นไม่สำเร็จ'
          );

        } finally {

          setLoading(false);
          setRefreshing(false);

        }
      },
      [selectedUserId]
    );


  /* =================================================
     INITIAL LOAD
  ================================================= */

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (profileMode && !storedUser?.user_id) navigate('/', { replace: true });
  }, [navigate, profileMode, storedUser?.user_id]);

  const loadCheckinControls = useCallback(async () => {
    if (!profileMode || !selectedUserId) return;
    try {
      const [groupsResponse, statusResponse] = await Promise.all([
        fetch(`${API_BASE}/api/groups`),
        fetch(`${API_BASE}/api/checkin/status/${selectedUserId}`)
      ]);
      const groupsData = await groupsResponse.json().catch(() => []);
      const statusData = await statusResponse.json().catch(() => ({}));
      const availableGroups = (Array.isArray(groupsData) ? groupsData : [])
        .filter(item => String(item.Group_Status || '').toUpperCase() === 'OPEN');
      setCheckinStatus(statusData);
      setCheckinGroupId(current => current || String(statusData?.group_id || availableGroups[0]?.id || ''));
    } catch (checkinError) {
      setCheckinMessage(checkinError.message || 'โหลดข้อมูล Check-in ไม่สำเร็จ');
    }
  }, [profileMode, selectedUserId]);

  useEffect(() => {
    loadCheckinControls();
  }, [loadCheckinControls]);

  const handleQrValue = useCallback(async rawValue => {
    try {
      setCheckinMessageType('info');
      const qrUrl = new URL(rawValue, window.location.origin);
      if (!/^\/join\/\d+$/i.test(qrUrl.pathname) || !qrUrl.searchParams.get('code')) {
        throw new Error('QR นี้ไม่ใช่ QR Check-in ของระบบ');
      }
      const qrGroupId = Number(qrUrl.pathname.split('/').filter(Boolean)[1]);
      const infoResponse = await fetch(
        `${API_BASE}/api/checkin/join/${qrGroupId}/info?code=${encodeURIComponent(qrUrl.searchParams.get('code'))}`
      );
      const groupData = await infoResponse.json().catch(() => ({}));
      if (!infoResponse.ok) throw new Error(groupData.message || 'QR Code ไม่ถูกต้อง');
      setCheckinGroupId(String(qrGroupId));
      const isCurrentGroup = Number(checkinStatus?.group_id) === qrGroupId;
      if (isCurrentGroup && checkinStatus?.status === 'HOLD') {
        const response = await fetch(`${API_BASE}/api/checkin/join/${qrGroupId}/checkout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินไม่สำเร็จ');
        setCheckinMessageType('success');
        setCheckinMessage(data.message);
      } else if (isCurrentGroup) {
        setCheckinMessageType('info');
        setCheckinMessage(`คุณเช็คอิน ${groupData.Group_Name} อยู่แล้ว`);
      } else {
        const response = await fetch(`${API_BASE}/api/checkin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: selectedUserId, Group_Name: groupData.Group_Name })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'เช็คอินด้วย QR ไม่สำเร็จ');
        localStorage.setItem('last_group_id', String(qrGroupId));
        setCheckinMessageType('success');
        setCheckinMessage(`สแกน QR และเช็คอิน ${groupData.Group_Name} สำเร็จ`);
      }
      await loadCheckinControls();
    } catch (scanError) {
      setCheckinMessageType('error');
      setCheckinMessage(scanError.message || 'อ่าน QR ไม่สำเร็จ');
    }
  }, [authToken, checkinStatus, loadCheckinControls, selectedUserId]);

  const openQrScanner = () => {
    if (window.isSecureContext && navigator.mediaDevices?.getUserMedia) {
      setQrScannerOpen(true);
      return;
    }
    setCheckinMessageType('error');
    setCheckinMessage('ไม่สามารถเปิดกล้องได้ กรุณาเปิดหน้านี้ผ่าน HTTPS และอนุญาตสิทธิ์กล้อง');
  };

  useEffect(() => {
    if (!qrScannerOpen || !qrVideoRef.current) return undefined;
    let cancelled = false;
    const reader = new BrowserQRCodeReader();
    setCheckinMessageType('info');
    setCheckinMessage('หันกล้องไปที่ QR Code');
    reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' } } },
      qrVideoRef.current,
      (result, error, controls) => {
        void error;
        if (result && !cancelled) {
          cancelled = true;
          controls.stop();
          setQrScannerOpen(false);
          handleQrValue(result.getText());
        }
      }
    ).then(controls => {
      qrControlsRef.current = controls;
    }).catch(error => {
      setQrScannerOpen(false);
      setCheckinMessageType('error');
      setCheckinMessage(error?.message?.includes('Permission')
        ? 'กรุณาอนุญาตการใช้กล้อง แล้วลองใหม่'
        : 'เปิดกล้องไม่สำเร็จ กรุณาตรวจสิทธิ์กล้องของเบราว์เซอร์');
    });
    return () => {
      cancelled = true;
      qrControlsRef.current?.stop();
      qrControlsRef.current = null;
    };
  }, [handleQrValue, qrScannerOpen]);


  /* =================================================
     SOCKET REALTIME
  ================================================= */

  useEffect(() => {

    if (!selectedGroupId) {
      return undefined;
    }

    const monitorSocket =
      createMonitorSocket();

    const joinGroup = () => {

      monitorSocket.emit(
        'join-group',
        selectedGroupId
      );

    };

    const refresh = payload => {

      const eventGroupId =
        payload?.groupId ??
        payload?.group_id;

      if (
        eventGroupId &&
        Number(eventGroupId) !==
          selectedGroupId
      ) {
        return;
      }

      loadHistory({
        silent: true
      });
    };

    const events = [
      'match-started',
      'match-ended',
      'match-result-updated',
      'rank-table-updated',
      'point-updated',
      'match-updated',
      'player-status-updated',
      'member-updated',
      'player-updated'
    ];

    monitorSocket.on(
      'connect',
      joinGroup
    );

    events.forEach(
      eventName =>
        monitorSocket.on(
          eventName,
          refresh
        )
    );

    if (
      monitorSocket.connected
    ) {
      joinGroup();
    }

    return () => {

      monitorSocket.off(
        'connect',
        joinGroup
      );

      events.forEach(
        eventName =>
          monitorSocket.off(
            eventName,
            refresh
          )
      );

      monitorSocket.disconnect();

    };

  }, [
    loadHistory,
    selectedGroupId
  ]);


  /* =================================================
     FALLBACK REFRESH
  ================================================= */

  useEffect(() => {

    const intervalId =
      window.setInterval(
        () => {
          loadHistory({
            silent: true
          });
        },
        10000
      );

    return () => {
      window.clearInterval(
        intervalId
      );
    };

  }, [loadHistory]);


  /* =================================================
     LOGIN
  ================================================= */

  const goToLogin = () => {

    const returnPath =
      `/player-history/${selectedGroupId}/${selectedUserId}`;

    const playerName =
      history?.player?.name || '';

    const loginUrl =
      `/?redirect=${encodeURIComponent(returnPath)}` +
      `&name=${encodeURIComponent(playerName)}`;

    navigate(loginUrl);

  };


  /* =================================================
     LOGOUT
  ================================================= */

  const handleLogout = () => {

    localStorage.removeItem(
      'token'
    );

    localStorage.removeItem(
      'user'
    );

    sessionStorage.removeItem(
      'loginRedirect'
    );

    setAuthToken(null);
    setAuthUser(null);

    setAvatarMessage(
      'ออกจากระบบแล้ว'
    );

  };

  const handleChangePassword = async event => {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage('รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน');
      return;
    }
    try {
      setPasswordBusy(true);
      setPasswordMessage('');
      const response = await fetch(`${API_BASE}/api/users/me/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify(passwordForm)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ');
      setPasswordMessage('เปลี่ยนรหัสผ่านแล้ว ✓');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (changeError) {
      setPasswordMessage(changeError.message);
    } finally {
      setPasswordBusy(false);
    }
  };

  const handleChangeName = async event => {
    event.preventDefault();
    const nextName = String(nameDraft || '').trim().replace(/\s+/g, ' ');
    if (nextName.length < 2) {
      setNameMessage('กรุณากรอกชื่ออย่างน้อย 2 ตัวอักษร');
      return;
    }
    try {
      setNameBusy(true);
      setNameMessage('');
      const response = await fetch(`${API_BASE}/api/users/me/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ name: nextName })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'เปลี่ยนชื่อไม่สำเร็จ');
      setHistory(current => current ? { ...current, player: { ...current.player, name: data.name } } : current);
      const updatedUser = { ...authUser, name: data.name };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setAuthUser(updatedUser);
      setNameDraft(data.name);
      setNameMessage('เปลี่ยนชื่อเรียบร้อยแล้ว ✓');
    } catch (changeError) {
      setNameMessage(changeError.message);
    } finally {
      setNameBusy(false);
    }
  };


  /* =================================================
     AVATAR CHANGE
  ================================================= */

  const handleAvatarChange =
    async event => {

      if (!isLoggedIn) {

        goToLogin();

        return;

      }

      if (!canEditAvatar) {

        setAvatarMessage(
          'บัญชีนี้ไม่มีสิทธิ์เปลี่ยนรูปของผู้เล่นคนนี้'
        );

        return;

      }

      const file =
        event.target.files?.[0];

      event.target.value = '';

      if (!file) {
        return;
      }

      if (
        !file.type.startsWith(
          'image/'
        )
      ) {

        setAvatarMessage(
          'กรุณาเลือกไฟล์รูปภาพ'
        );

        return;

      }

      if (
        file.size >
        10 * 1024 * 1024
      ) {

        setAvatarMessage(
          'รูปต้นฉบับต้องมีขนาดไม่เกิน 10 MB'
        );

        return;

      }

      try {

        setUploadingAvatar(true);
        setAvatarMessage('');

        const dataUrl =
          await resizeAvatar(file);

        const response =
          await fetch(
            `${API_BASE}/api/users/${selectedUserId}/avatar`,
            {
              method: 'PUT',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${authToken}`
              },

              body: JSON.stringify({
                dataUrl
              })
            }
          );

        const data =
          await response
            .json()
            .catch(() => ({}));

        if (!response.ok) {

          throw new Error(
            data.message ||
            'อัปโหลดรูปไม่สำเร็จ'
          );

        }

        setHistory(current => {

          if (!current) {
            return current;
          }

          return {
            ...current,

            player: {
              ...current.player,

              avatarUrl:
                data.avatarUrl
            }
          };

        });

        setAvatarMessage(
          'เพิ่มรูปผู้เล่นแล้ว ✨'
        );

      } catch (uploadError) {

        console.error(
          '[AVATAR UPLOAD]',
          uploadError
        );

        setAvatarMessage(
          uploadError.message ||
          'อัปโหลดรูปไม่สำเร็จ'
        );

      } finally {

        setUploadingAvatar(false);

      }

    };


  /* =================================================
     LOADING
  ================================================= */

  if (loading) {

    return (
      <main className="player-history-page loading">
        กำลังโหลดประวัติผู้เล่น...
      </main>
    );

  }


  /* =================================================
     RENDER
  ================================================= */

  return (

    <main className={`player-history-page profile-modern ${profileMode ? 'profile-owner' : 'history-public'}`}>

      {/* ================= HERO ================= */}

      <header className="player-history-hero">

        <div className="player-history-profile">

          {/* ================= AVATAR ================= */}

          <div className="player-avatar-wrap">

            <div
              className="player-history-avatar avatar-placeholder"
              aria-hidden={
                !history?.player?.avatarUrl
              }
            >

              {
                history?.player?.name
                  ?.trim()
                  ?.charAt(0) ||
                '🏸'
              }

              {history?.player?.avatarUrl && (

                <img
                  className="player-history-avatar-image"

                  src={
                    getAvatarSource(
                      history.player.avatarUrl
                    )
                  }

                  alt={
                    `รูปของ ${history.player.name}`
                  }

                  onError={event => {
                    event.currentTarget.style.display =
                      'none';
                  }}
                />

              )}

            </div>


            <span
              className="avatar-sparkle"
              aria-hidden="true"
            >
              ✦
            </span>


            {/* ================= AVATAR ACTION ================= */}

            {canEditAvatar ? (

              <>

                <input
                  ref={avatarInputRef}
                  className="avatar-file-input"
                  type="file"

                  accept="image/jpeg,image/png,image/webp"

                  onChange={
                    handleAvatarChange
                  }
                />


                <button
                  type="button"
                  className="avatar-upload-button"

                  disabled={
                    uploadingAvatar
                  }

                  onClick={() =>
                    avatarInputRef
                      .current
                      ?.click()
                  }
                >

                  {
                    uploadingAvatar
                      ? '…'
                      : '📷'
                  }

                  <span>
                    {
                      uploadingAvatar
                        ? 'กำลังเพิ่มรูป'
                        : 'เพิ่มรูป'
                    }
                  </span>

                </button>

              </>

            ) : !isLoggedIn ? (

              <button
                type="button"
                className="avatar-login-button"
                onClick={
                  goToLogin
                }
              >
                🔐 เข้าสู่ระบบเพื่อเพิ่มรูป
              </button>

            ) : (

              <div className="avatar-owner-message">
                🔒 เฉพาะเจ้าของบัญชีเท่านั้น
              </div>

            )}

          </div>


          {/* ================= PLAYER INFO ================= */}

          <div className="player-history-name">

            <span>
              PLAYER MATCH HISTORY
            </span>

            <div className="player-history-name-row">
              <h1>
                {
                  history?.player?.name ||
                  'ประวัติผู้เล่น'
                }
              </h1>

              {isOwner && (
                <button
                  type="button"
                  className="history-password-button"
                  onClick={() => {
                    setPasswordMessage('');
                    setNameMessage('');
                    setNameDraft(history?.player?.name || authUser?.name || '');
                    setPasswordOpen(true);
                  }}
                >
                  ✎ แก้ไขโปรไฟล์
                </button>
              )}
            </div>

            <p>

              มือ{' '}

              {
                history?.player?.levelRank ||
                '-'
              }

              {' • '}

              {
                history?.player?.point ||
                0
              }

              {' คะแนน • ID '}

              {selectedUserId}

            </p>


            {avatarMessage && (

              <small className="avatar-message">
                {avatarMessage}
              </small>

            )}

          </div>

        </div>


        {/* ================= NAV ================= */}

      <nav className="player-history-actions">

  {/* ================= MONITOR ================= */}

  <button
    type="button"
    className="history-monitor-button"
    onClick={() =>
      navigate(
        '/monitor'
      )
    }
  >
    <span className="history-monitor-icon">
      🏸
    </span>

    <span className="history-monitor-copy">
      <strong>Monitor Match</strong>
      <small>กลับหน้าคิวการแข่งขัน</small>
    </span>

    <span className="history-monitor-arrow">
      →
    </span>
  </button>

  {/* ================= RANK ================= */}

  <button
    type="button"
    className="history-rank-button"
    onClick={() =>
      navigate('/rank/')
    }
  >
    🏆 Rank Table
  </button>


  {/* ================= REFRESH ================= */}

  <button
    type="button"
    className="history-refresh"
    disabled={refreshing}
    onClick={() =>
      loadHistory({
        silent: true
      })
    }
  >
    {refreshing
      ? '⏳ กำลังอัปเดต...'
      : '↻ อัปเดต'}
  </button>


  {/* ================= LOGOUT ================= */}

  {isLoggedIn && (

    <button
      type="button"
      className="history-logout-button"
      onClick={handleLogout}
    >
      🚪 Logout
    </button>

  )}

</nav>

      </header>

      {profileMode && isOwner && (
        <section className="profile-checkin-panel" aria-label="เช็คอินตีแบด">
          <div className="profile-checkin-heading">
            <div>
              <strong>🏸 Check-in วันนี้</strong>
              <small>
                {checkinStatus?.hasMembership
                  ? `${checkinStatus.Group_Name} • ${checkinStatus.status}`
                  : 'เลือก Group หรือสแกน QR เพื่อเช็คอิน'}
              </small>
            </div>
            {checkinStatus?.status && (
              <span className={`profile-checkin-status status-${String(checkinStatus.status).toLowerCase()}`}>
                {checkinStatus.status}
              </span>
            )}
          </div>

          <div className="profile-checkin-hint">
            <span aria-hidden="true">▣</span>
            สแกน QR เพื่อ Check-in ได้จากปุ่มกล้องตรงกลางด้านล่าง
          </div>
          {isAdmin && (
            <button type="button" className="reward-admin-entry" onClick={() => navigate('/rewards')}>
              🛡 จัดการกิจกรรม โปรโมชัน และของรางวัล
            </button>
          )}

          {checkinMessage && (
            <p className={`profile-checkin-message is-${checkinMessageType}`} role={checkinMessageType === 'error' ? 'alert' : 'status'}>
              {checkinMessageType === 'error' ? '⚠️ ' : ''}{checkinMessage}
            </p>
          )}
        </section>
      )}

      {qrScannerOpen && (
        <div className="profile-qr-backdrop" role="dialog" aria-modal="true" aria-label="สแกน QR Check-in">
          <div className="profile-qr-modal">
            <header>
              <strong>📷 สแกน QR Check-in</strong>
              <button type="button" onClick={() => setQrScannerOpen(false)}>×</button>
            </header>
            <div className="profile-qr-camera">
              <video ref={qrVideoRef} muted playsInline />
              <span aria-hidden="true" />
            </div>
            <p>วาง QR Code ให้อยู่ภายในกรอบ ระบบจะอ่านให้อัตโนมัติ</p>
          </div>
        </div>
      )}

      {passwordOpen && (
        <div className="history-password-backdrop" role="presentation" onMouseDown={() => setPasswordOpen(false)}>
          <form className="history-password-modal" onSubmit={handleChangePassword} onMouseDown={event => event.stopPropagation()}>
            <header>
              <strong>แก้ไขโปรไฟล์</strong>
              <button type="button" onClick={() => setPasswordOpen(false)}>×</button>
            </header>
            <div className="history-profile-name-form">
              <label htmlFor="profile-display-name">ชื่อผู้เล่น</label>
              <small>แนะนำให้ใช้ “ชื่อ + ฉายา” เพื่อให้เพื่อนค้นหาและจำได้ง่าย เช่น น้ากิ๊ก สายบุก</small>
              <input
                id="profile-display-name"
                type="text"
                minLength="2"
                maxLength="100"
                placeholder="ชื่อ + ฉายา"
                value={nameDraft}
                onChange={event => setNameDraft(event.target.value)}
                required
              />
              {nameMessage && <small className="history-password-message">{nameMessage}</small>}
              <button className="history-password-submit" type="button" disabled={nameBusy} onClick={handleChangeName}>
                {nameBusy ? 'กำลังบันทึก…' : 'บันทึกชื่อใหม่'}
              </button>
            </div>
            <div className="history-profile-divider"><span>เปลี่ยนรหัสผ่าน</span></div>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="รหัสผ่านเดิม"
              value={passwordForm.currentPassword}
              onChange={event => setPasswordForm(current => ({ ...current, currentPassword: event.target.value }))}
              required
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="รหัสผ่านใหม่อย่างน้อย 4 ตัว"
              minLength="4"
              value={passwordForm.newPassword}
              onChange={event => setPasswordForm(current => ({ ...current, newPassword: event.target.value }))}
              required
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="ยืนยันรหัสผ่านใหม่"
              minLength="4"
              value={passwordForm.confirmPassword}
              onChange={event => setPasswordForm(current => ({ ...current, confirmPassword: event.target.value }))}
              required
            />
            {passwordMessage && <small className="history-password-message">{passwordMessage}</small>}
            <button className="history-password-submit" type="submit" disabled={passwordBusy}>
              {passwordBusy ? 'กำลังบันทึก…' : 'บันทึกรหัสผ่านใหม่'}
            </button>
          </form>
        </div>
      )}


      {/* ================= ERROR ================= */}

      {error && (

        <div className="player-history-error">
          ❌ {error}
        </div>

      )}


      {/* ================= HISTORY ================= */}

      {history && (

        <>

          {/* ================= SUMMARY ================= */}

          <section
            className="history-summary-grid"
            aria-label="สรุปผลการแข่งขัน"
          >

            <article className="overall">
              <span>💖 Win rate รวมทุก Group</span>
              <strong>
                {history.summary.winRate}%
              </strong>
            </article>

            <article className="completed">
              <span>🏸 เล่นจบ</span>
              <strong>
                {
                  history.summary
                    .completedMatches
                }
              </strong>
            </article>

            <article className="win">
              <span>🌟 ชนะ</span>
              <strong>
                {history.summary.wins}
              </strong>
            </article>

            <article className="draw">
              <span>🤝 เสมอ</span>
              <strong>
                {history.summary.draws}
              </strong>
            </article>

            <article className="loss">
              <span>🌈 แพ้</span>
              <strong>
                {history.summary.losses}
              </strong>
            </article>

          </section>


          {/* ================= MATCH DETAIL ================= */}

          <section className="history-section">

            <header>

              <div>

                <h2>
                  🏸 Match Records
                </h2>

                <p>
                  ทีมเดียวกัน คู่แข่ง สนาม และผลการแข่งขัน
                </p>

              </div>

            </header>


            <div className="history-match-list">

              {history.matches.length > 0
                ? history.matches.map(
                    match => (

                      <article
                        className={
                          `history-match result-${String(
                            match.result ||
                            'PENDING'
                          ).toLowerCase()}`
                        }

                        key={
                          match.matchId
                        }
                      >

                        <div className="history-match-meta">

                          <span>
                            {
                              match.groupName
                            }
                          </span>

                          <strong>

                            Match #

                            {
                              match.groupMatchNo ||
                              match.matchId
                            }

                            {' • '}

                            Court{' '}

                            {
                              match.courtNo ||
                              '-'
                            }

                          </strong>

                          <small>

                            {
                              formatDateTime(
                                match.playedAt
                              )
                            }

                          </small>

                        </div>


                        <b className="history-result">

                          {
                            RESULT_LABELS[
                              match.result
                            ] ||
                            match.result ||
                            '-'
                          }

                        </b>

                        <strong
                          className={`history-point-change ${
                            Number(match.pointChange || 0) > 0
                              ? 'is-positive'
                              : Number(match.pointChange || 0) < 0
                                ? 'is-negative'
                                : 'is-zero'
                          }`}
                        >
                          {match.pointChange == null
                            ? 'แต้มรอสรุป'
                            : `แต้ม ${Number(match.pointChange) > 0 ? '+' : ''}${Number(match.pointChange)}`}
                        </strong>


                        <div className="history-people">

                          <span>

                            คู่ทีม

                            <strong>

                              {
                                match.teammates
                                  ?.map(
                                    person =>
                                      person.name
                                  )
                                  .join(', ') ||
                                '-'
                              }

                            </strong>

                          </span>


                          <span>

                            คู่แข่ง

                            <strong>

                              {
                                match.opponents
                                  ?.map(
                                    person =>
                                      person.name
                                  )
                                  .join(', ') ||
                                '-'
                              }

                            </strong>

                          </span>

                        </div>

                      </article>

                    )
                  )
                : (

                  <div className="history-empty">
                    ยังไม่มีประวัติ Match
                  </div>

                )}

            </div>

          </section>

          {profileMode && (
            <BottomNavigation
              active="profile"
              onMonitor={() => navigate('/monitor')}
              onMatches={() =>
                document.querySelector('.history-section')?.scrollIntoView({
                  behavior: 'smooth'
                })
              }
              onScan={openQrScanner}
              onReward={() => navigate('/rewards')}
              onProfile={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          )}

          {!profileMode && (
            <nav className="history-public-bottom-nav" aria-label="เมนูประวัติผู้เล่น">
              <button type="button" onClick={() => navigate('/monitor')}>
                <span>▣</span><small>Monitor</small>
              </button>
              <button type="button" onClick={() => navigate('/rank/')}>
                <span>🏆</span><small>Rank</small>
              </button>
              <button type="button" disabled={refreshing} onClick={() => loadHistory({ silent: true })}>
                <span>↻</span><small>{refreshing ? 'กำลังอัปเดต' : 'อัปเดต'}</small>
              </button>
              {isLoggedIn && (
                <button type="button" onClick={handleLogout}>
                  <span>⇥</span><small>Logout</small>
                </button>
              )}
            </nav>
          )}

        </>

      )}

    </main>

  );

}


export default PlayerHistory;
